import { t } from '../../shared/config/translations'
import type { ClientUser } from '../../shared/types/user'
import { IS_NATIVE_APP } from '../config/environment'
import type { PageHandle, Route } from '../router'
import {
	ensureFreshCollection,
	getCollectionStoreState,
	hasUsableCollection,
	invalidateCollectionState,
	refreshCollectionStatus,
	resetCollectionSession,
	setCollectionForeground,
	subscribeCollectionStore,
	syncCollectionFromDiscogs,
} from '../stores/collectionStore'
import { onAppForegroundChange } from '../utils/appLifecycle'
import { updateActiveNavItem } from '../utils/navState'
import {
	captureScroll,
	restoreScroll,
	type ScrollPosition,
} from '../utils/scrollPosition'
import { collectionRefreshHelpText } from './collectionRefreshHelpTemplates'
import { collectionSyncStatusText } from './collectionSyncStatus'
import {
	startFriendRequestIndicator,
	stopFriendRequestIndicator,
} from './FriendRequestIndicator'
import { renderSpinner } from './Loader'
import {
	footerTemplate,
	headerTemplate,
	protectedLayoutTemplate,
} from './layoutTemplates'
import { NativeRefreshControl } from './NativeRefreshControl'
import { bindNavEvents, renderNav } from './Nav'
import { checkOverlayValidity, closeOverlay } from './Overlay'
import { bindPreviewPlayer, disposePreviewPlayer } from './PreviewPlayer'
import { previewPlayerTemplate } from './previewPlayerTemplates'
import recordPlayerTemplate from './recordPlayerTemplates'
import { getReleaseUpdateDetail } from './release/releaseEvents'

type CachedPage = {
	route: Route
	host: HTMLElement
	abort: AbortController
	handle?: PageHandle
	failed?: boolean
	refreshing?: Promise<void>
	activating?: Promise<void>
	revision: number
	position: ScrollPosition
	visible: boolean
}

type RefreshReason = 'pull' | 'snapshot-changed'

const REFRESH_TITLE_INTERVAL_MS = 60 * 1000

/** Owns one mounted view per route for the lifetime of an authenticated session. */
export default class ProtectedLayout {
	private readonly pages = new Map<string, CachedPage>()
	private readonly content: HTMLElement
	private readonly readiness: HTMLElement
	private readonly refreshControl?: NativeRefreshControl
	private readonly events = new AbortController()
	private readonly unsubscribe: () => void
	private readonly removeLifecycle: () => void
	private refreshTitleTimer?: ReturnType<typeof setInterval>
	private active?: CachedPage
	private sequence = 0
	private disposed = false
	private foreground = true

	constructor(
		root: HTMLElement,
		readonly user: ClientUser,
	) {
		root.innerHTML = protectedLayoutTemplate({
			contentHtml: '<section id="inner-content"></section>',
			footerHtml: footerTemplate(),
			headerHtml: headerTemplate(),
			navHtml: renderNav(),
			previewPlayerHtml: previewPlayerTemplate(),
		})
		const content = root.querySelector<HTMLElement>('#inner-content')
		if (!content) throw new Error('Incomplete app layout')

		this.content = content
		this.readiness = document.createElement('section')
		this.readiness.className = 'collection-readiness'
		this.readiness.hidden = true
		this.readiness.setAttribute('role', 'status')
		this.content.append(this.readiness)
		if (IS_NATIVE_APP) {
			this.refreshControl = new NativeRefreshControl(() =>
				this.refresh('pull'),
			)
			// Keeps relative "Synced …" wording current between store changes.
			this.refreshTitleTimer = setInterval(
				() => this.updateRefreshTitle(),
				REFRESH_TITLE_INTERVAL_MS,
			)
		}

		bindPreviewPlayer()
		bindNavEvents()
		startFriendRequestIndicator()
		document.documentElement.classList.add('protected-app')
		const { signal } = this.events
		window.addEventListener(
			'collection:release-updated',
			(event) => {
				const { confirmed, reason } = getReleaseUpdateDetail(event)
				// Optimistic UI updates must not refetch the still-unmodified server data.
				if (confirmed && (reason === 'favorite' || reason === 'rating'))
					invalidateCollectionState()
			},
			{ signal },
		)
		this.readiness.addEventListener(
			'click',
			(event) => {
				if (
					(event.target as Element).closest('button') &&
					this.active
				) {
					void this.activate(this.active, true)
				}
			},
			{ signal },
		)
		const scrollButton = document.getElementById('scroll-top-btn')
		scrollButton?.addEventListener(
			'click',
			() => window.scrollTo({ top: 0, behavior: 'smooth' }),
			{ signal },
		)
		window.addEventListener(
			'scroll',
			() =>
				scrollButton?.classList.toggle(
					'is-visible',
					window.scrollY > 600,
				),
			{ passive: true, signal },
		)
		this.unsubscribe = subscribeCollectionStore(() => this.reconcile())
		this.removeLifecycle = onAppForegroundChange((active) =>
			this.setForeground(active),
		)
		// Readiness failures are displayed by renderControls, including on retry.
		void ensureFreshCollection().catch(() => {})
	}

	async show(path: string, route: Route): Promise<void> {
		const sequence = ++this.sequence
		closeOverlay(false)
		if (this.active) {
			if (this.active.visible)
				this.active.position = captureScroll(this.active.host)
			this.hide(this.active)
		}

		this.refreshControl?.cancel()
		let page = this.pages.get(path)
		if (!page) {
			const host = document.createElement('section')
			host.className = 'route-view'
			host.hidden = true
			host.dataset.route = path
			this.content.append(host)
			page = {
				route,
				host,
				abort: new AbortController(),
				revision: -1,
				position: { y: 0 },
				visible: false,
			}
			this.pages.set(path, page)
		}

		this.active = page
		updateActiveNavItem(path)
		this.renderControls()
		await this.activate(page)
		if (sequence !== this.sequence || this.disposed || !page.visible) return
		await new Promise<void>((resolve) =>
			requestAnimationFrame(() => resolve()),
		)
		if (sequence === this.sequence && page.visible && !this.disposed)
			restoreScroll(page.host, page.position)
	}

	private hide(page: CachedPage): void {
		if (page.visible) page.handle?.hide()
		page.visible = false
		page.host.hidden = true
	}

	private reconcile(): void {
		if (this.disposed || !this.active) return
		const page = this.active
		// Retain expired views in memory, but do not display their snapshot data.
		if (page.route.collectionDependent && !hasUsableCollection()) {
			closeOverlay()
			if (page.visible) page.position = captureScroll(page.host)
			this.hide(page)
		}
		this.renderControls()
		if (!this.foreground) return
		if (page.activating || page.refreshing) return
		if (
			page.route.collectionDependent &&
			!hasUsableCollection() &&
			getCollectionStoreState().phase === 'blocked'
		)
			return
		if (
			!page.handle ||
			!page.visible ||
			(page.route.refreshable &&
				page.revision !== getCollectionStoreState().readiness.revision)
		)
			void this.activate(page)
	}

	private activate(page: CachedPage, retry = false): Promise<void> {
		if (page.activating) return page.activating
		const work = (async () => {
			// Publish page.activating before store notifications can re-enter here.
			await Promise.resolve()
			page.failed = false
			try {
				if (page.route.collectionDependent)
					await ensureFreshCollection()
				if (
					this.disposed ||
					this.active !== page ||
					page.abort.signal.aborted
				)
					return
				if (!page.handle) {
					const collection = getCollectionStoreState().readiness
					page.handle = await page.route.component.mount({
						containerEl: page.host,
						user: this.user,
						collection,
						signal: page.abort.signal,
					})
					page.revision = collection.revision
				}

				// Mounting may outlive both navigation and the snapshot deadline.
				if (page.route.collectionDependent && !hasUsableCollection())
					await ensureFreshCollection()
				if (
					this.disposed ||
					this.active !== page ||
					page.abort.signal.aborted
				)
					return
				if (
					retry ||
					page.revision !==
						getCollectionStoreState().readiness.revision
				) {
					await this.refreshPage(page, 'snapshot-changed')
				}
				if (
					this.disposed ||
					this.active !== page ||
					page.abort.signal.aborted
				)
					return
				page.host.hidden = false
				page.visible = true
				if (this.foreground) page.handle.show()
				this.readiness.hidden = true
			} catch {
				page.failed = true
				if (!page.handle) {
					page.abort.abort()
					page.abort = new AbortController()
					page.host.replaceChildren()
				}
			} finally {
				page.activating = undefined
				this.renderControls()
			}
		})()
		page.activating = work
		return work
	}

	private async refresh(reason: RefreshReason): Promise<void> {
		const page = this.active
		if (!page?.handle?.refresh || page.activating) return
		// A pull asks Discogs for changes; the new snapshot refreshes the page.
		if (reason === 'pull' && page.route.collectionDependent) {
			const synced = await syncCollectionFromDiscogs()
			if (this.disposed || this.active !== page) return
			if (synced) {
				await (page.activating ?? page.refreshing)
				return
			}
			if (page.activating) return page.activating
		}
		return this.refreshPage(page, reason)
	}

	private refreshPage(
		page: CachedPage,
		reason: RefreshReason,
	): Promise<void> {
		if (page.refreshing) return page.refreshing
		if (page.visible) page.position = captureScroll(page.host)
		page.handle?.hide()
		const work = (async () => {
			await Promise.resolve()
			if (page.route.collectionDependent && reason === 'pull') {
				await refreshCollectionStatus()
			}

			// Saves can finish while a refresh is running. Settle only after reading
			// the latest revision, including when an older query was invalidated.
			while (!this.disposed && !page.abort.signal.aborted) {
				if (page.route.collectionDependent)
					await ensureFreshCollection()
				if (this.disposed || page.abort.signal.aborted) return
				const revision = getCollectionStoreState().readiness.revision
				try {
					await page.handle?.refresh?.()
				} catch (error) {
					if (
						revision ===
						getCollectionStoreState().readiness.revision
					)
						throw error
					continue
				}
				if (revision !== getCollectionStoreState().readiness.revision)
					continue
				page.revision = revision
				break
			}
			if (this.disposed || page.abort.signal.aborted) return
			page.failed = false
			if (this.active === page) {
				page.host.hidden = false
				page.visible = true
				restoreScroll(page.host, page.position)
			}
		})()
			.catch((error) => {
				page.failed = true
				throw error
			})
			.finally(() => {
				if (page.refreshing === work) page.refreshing = undefined
				if (
					!this.disposed &&
					this.foreground &&
					this.active === page &&
					page.visible &&
					!page.activating
				)
					page.handle?.show()
				this.renderControls()
			})
		page.refreshing = work
		this.renderControls()
		return work
	}

	private renderControls(): void {
		if (this.disposed) return
		const page = this.active
		this.refreshControl?.setState(
			!!page?.route.refreshable && !!page.handle && page.visible,
			!!page?.refreshing || !!page?.activating,
		)
		this.updateRefreshTitle()
		if (!page || page.visible) {
			this.readiness.hidden = true
			return
		}
		this.readiness.hidden = false
		const state = getCollectionStoreState()
		if (
			page.failed ||
			(state.phase === 'blocked' && page.route.collectionDependent)
		) {
			this.renderFailure()
			return
		}

		const job = state.status?.job
		if (job) {
			this.readiness.removeAttribute('aria-label')
			let progress = this.readiness.querySelector<HTMLParagraphElement>(
				'.collection-import-progress',
			)
			if (!progress) {
				const player = document.createElement('div')
				player.setAttribute('aria-hidden', 'true')
				player.innerHTML = recordPlayerTemplate()
				progress = document.createElement('p')
				progress.className = 'collection-import-progress'
				const help = document.createElement('p')
				help.className = 'collection-import-help'
				help.textContent = collectionRefreshHelpText()
				this.readiness.replaceChildren(player, progress, help)
			}
			progress.textContent = `${t('progress.importing')}: ${job.current}/${job.total || '?'}`
			const arm = this.readiness.querySelector<SVGGElement>('#armbase')
			const fraction =
				job.total > 0
					? Math.max(0, Math.min(1, job.current / job.total))
					: 0
			if (arm) {
				arm.style.transform = `translate(280px, 24px) rotate(${17 + 18 * fraction}deg)`
			}
			return
		}
		this.readiness.setAttribute('aria-label', t('refresh.refreshing'))
		if (!this.readiness.querySelector('.spinner')) {
			this.readiness.replaceChildren()
			renderSpinner(this.readiness).setAttribute('aria-hidden', 'true')
		}
	}

	private updateRefreshTitle(): void {
		this.refreshControl?.setTitle(
			this.active?.route.collectionDependent
				? collectionSyncStatusText()
				: '',
		)
	}

	private renderFailure(): void {
		this.readiness.hidden = false
		this.readiness.removeAttribute('aria-label')
		this.readiness.replaceChildren()
		const message = document.createElement('p')
		message.textContent = t('refresh.failed')
		const retry = document.createElement('button')
		retry.type = 'button'
		retry.textContent = t('refresh.retry')
		this.readiness.append(message, retry)
	}

	setForeground(active: boolean): void {
		if (active) checkOverlayValidity()
		this.foreground = active
		setCollectionForeground(active)
		if (!active) {
			this.active?.handle?.hide()
			this.refreshControl?.cancel()
			return
		}
		if (!hasUsableCollection()) {
			this.reconcile()
			void ensureFreshCollection().catch(() => {})
		} else {
			this.reconcile()
			if (!this.active?.refreshing && !this.active?.activating)
				this.active?.handle?.show()
		}
	}

	dispose(): void {
		this.disposed = true
		this.sequence++
		this.unsubscribe()
		this.removeLifecycle()
		this.events.abort()
		clearInterval(this.refreshTitleTimer)
		this.refreshControl?.dispose()
		closeOverlay()
		for (const page of this.pages.values()) {
			page.abort.abort()
			page.host.remove()
		}
		this.pages.clear()
		stopFriendRequestIndicator()
		disposePreviewPlayer()
		resetCollectionSession()
		document.documentElement.classList.remove('protected-app')
	}
}
